import React from "react";
import { Box, keyframes, Paper, SxProps, Theme } from "@mui/material";
import { useUser } from "../../api/useAuth";
import { ROLES } from "../../utils/constants";
import { UserFromAPI } from "../../types/auth.types";

export type WarningBoxVisibilityList = Array<"reception" | "technician">

type WarningBaseProps = {
	wrap: boolean;
	warnings: Array<{ show: boolean; color: string; visibleTo: WarningBoxVisibilityList; content: React.ReactNode }>;
};

export const WarningBox = ({ warnings, wrap }: WarningBaseProps) => {
	const { data: user } = useUser();
	const flexWrap = wrap ? "wrap" : "nowrap";

	if (!user?.role?.id) return null;
	if (warnings.every((warning) => !warning.show)) return null;

	const blinkAnimation = keyframes`
        0% { opacity: 1; }
        50% { opacity: 0.4; }
        100% { opacity: 1; }
    `;
	const animation = `${blinkAnimation} 1.5s infinite`;

	return (
		<Box sx={{ width: "fit-content" }}>
			<Box
				p={0}
				zIndex={1}
				position="relative"
				display="flex"
				flexWrap={flexWrap}
				flex={1}
				m={0.3}
				width="fit-content"
				gap={2}
			>
				{warnings
					.filter(({ show, visibleTo }) => show && showWarningToUser(user, visibleTo))
					.map(({ color, content }, index) => (
						<GlowBox
							key={index}
							color={color === "error" ? "#f44336" : "#ff9800"}
							animation={animation}
							sx={{ p: 0.6, flexWrap: "inherit", width: "fit-content" }}
						>
							<Paper
								elevation={0}
								sx={{
									py: 1,
									px: 2,
									height: "100%",
									display: "flex",
									borderRadius: "20px",
									flexWrap: "inherit",
									opacity: 0.9,
									justifyContent: "center",
									alignItems: "center",
									width: "fit-content",
								}}
							>
								{content}
							</Paper>
						</GlowBox>
					))}
			</Box>
		</Box>
	);
};

type GlowBoxProps = {
	children: React.ReactNode;
	animation: string;
	color?: string;
	sx?: SxProps<Theme>;
};

const GlowBox = ({ children, animation, color = "#ff9800", sx = {} }: GlowBoxProps) => {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				textShadow: "0 1px 0 rgba(0, 0, 0, 0.3)",
				position: "relative",
				zIndex: 1,
				width: "100%",
				borderRadius: "20px",
				backgroundColor: color,
				overflow: "visible",
				...sx,
				"&::before": {
					content: '""',
					animation: animation,
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					fontWeight: "bold",
					borderRadius: "20px",
					position: "absolute",
					zIndex: 0,
					mixBlendMode: "screen",
					border: `1px solid ${color}`,
					boxShadow: `
            0 0 2px 1px ${color},
            0 0 6px 2px ${color},
            0 0 8px 3px ${color}
          `,
				},
			}}
		>
			{children}
		</Box>
	);
};

export default GlowBox;

const showWarningToUser = (user: UserFromAPI, warningTo: WarningBoxVisibilityList) => {
  	const isRecepconistRelated =
		user?.role?.id === ROLES.reception_director ||
		user?.role?.id === ROLES.recepcionist ||
		user?.role?.id === ROLES.substituion_joana_temp ||
		user?.role?.id === ROLES.admin ||
		user?.role?.id === ROLES.shilan;

  if (warningTo.includes("reception") && isRecepconistRelated) return true
  
  // TODO Custom logic here
  if (warningTo.includes("technician") && true ) return true
};
